import { ResearchInPracticeSection } from '../components/sections/ResearchInPracticeSection';
import React, { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Container, Button } from '../components/ui';
import { ServiceCard } from '../components/sections';
import { INDUSTRIES, type Industry } from '../data/industries';
import { WHAT_WE_DO_SERVICES } from '../data/whatWeDo';
import { NotFound } from './NotFound';

/*
 * IndustryDetail
 * --------------
 * Per-industry landing page rendered at `/industries/<slug>`. The
 * slug is looked up in the shared `INDUSTRIES` catalog; if it doesn't
 * match, we bounce to the 404 route so an old / mistyped URL doesn't
 * leave the visitor on a half-empty page.
 *
 * Sections (top-to-bottom):
 *   1. Sticky breadcrumbs (Home > Industries > <Industry>).
 *   2. Cover-video hero with an overlay card — title, tagline, and a
 *      "Connect with our experts" CTA that routes to the Contact
 *      form pre-tagged with this industry slug.
 *   3. "What sets us apart" section — video preview on the left,
 *      heading + narrative on the right (mirrors the Zensar
 *      reference layout).
 *   4. "Explore our services" grid — the same 11-service grid we
 *      render on /services and /services/<slug>, so a visitor who's
 *      just learned about our industry approach can see the shape of
 *      the actual offerings that would ship into their engagement.
 *
 * The hero + services grid deliberately reuse the same visual
 * grammar as the /services family so the site's "chapter" pages all
 * read as one product.
 */

const HEADER_OFFSET_PX = 80; // matches Header's h-20

export const IndustryDetail: React.FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const industry = INDUSTRIES.find((i) => i.id === industryId);

  // Unknown / mistyped slug → render the site-wide 404 inline
  // rather than redirecting to `/404` (which isn't a real route),
  // so the URL bar stays honest and the visitor can just hit
  // back to their previous page.
  if (!industry) {
    return <NotFound />;
  }

  return (
    <>
      {/* ═════════════════ Breadcrumbs strip ═════════════════
          Same sticky-under-the-header pattern as /services and
          /services/<slug>: an 80 px spacer to clear the fixed
          header, then a `sticky top-20 z-40` bar so the trail stays
          anchored while the visitor scrolls through the hero and
          the rest of the page. */}
      <div style={{ height: `${HEADER_OFFSET_PX}px` }} aria-hidden />
      <div className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm">
        <Container className="py-4">
          <Breadcrumbs industry={industry} />
        </Container>
      </div>

      {/* ═════════════════ Hero cover ═════════════════ */}
      <IndustryHero industry={industry} />

      {/* ═════════════════ What sets us apart ═════════════════ */}
      <WhatSetsUsApartSection industry={industry} />

      {/* ═════════════════ Explore Our Services ═════════════════ */}
      <ExploreOurServicesSection />
      {/* Show the full research catalog, as on IstaLabs. */}
      <ResearchInPracticeSection />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Breadcrumbs: React.FC<{ industry: Industry }> = ({ industry }) => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <Link
      to="/industries"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Industries
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span aria-current="page" className="font-semibold text-navy-900">
      {industry.title}
    </span>
  </nav>
);

/**
 * Cover video + centred overlay card. Height math + card sizing
 * intentionally mirror the /services and /services/<slug> heroes so
 * every "chapter" page on the site opens with the same visual
 * grammar. The `calc(56.25vw - 200px)` clip is the source webm's
 * native 16:9 aspect minus 200 px, matching the reduction we've
 * applied elsewhere.
 */
const IndustryHero: React.FC<{ industry: Industry }> = ({ industry }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [industry.mediaUrl]);

  return (
    <section className="relative bg-navy-950">
      <div
        className="responsive-hero relative w-full overflow-hidden"
      >
        <video
          key={industry.mediaUrl}
          ref={videoRef}
          src={industry.mediaUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div aria-hidden className="absolute inset-0 bg-black/35" />

        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={
              'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl ' +
              'bg-white rounded-2xl border border-navy-100 ' +
              'shadow-[0_25px_70px_-15px_rgba(2,8,20,0.6)] ' +
              'p-5 sm:p-8 md:p-10 lg:p-12'
            }
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-900 leading-tight mb-4 md:mb-6">
              {industry.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-[22px] text-navy-700 leading-relaxed mb-7 md:mb-9">
              {industry.heroTagline}
            </p>
            {/* Funnels to the Contact form with the industry pre-
                selected so the sales team knows the visitor's context
                the moment the message hits the inbox. */}
            <Link
              to={`/contact?inquiry=industry&industry=${industry.id}`}
            >
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Connect with our experts
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * "What sets us apart" — split layout with the industry cover clip
 * on the left and the industry-specific narrative on the right.
 * Same voice as the Zensar reference, but written for Istatec's
 * positioning (mid-scale, senior + best-sourced engineers, IstaLabs
 * R&D). The clip is intentionally the same asset as the hero so we
 * don't need bespoke imagery per industry — the video is the same,
 * the framing and surrounding narrative are what differentiate the
 * two placements.
 */
const WhatSetsUsApartSection: React.FC<{ industry: Industry }> = ({
  industry,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [industry.mediaUrl]);

  return (
    <section className="bg-navy-50/60 py-16 md:py-24 lg:py-28 overflow-x-clip">
      <div className="site-wide-container">
        <motion.h2
          className="heading-lg text-navy-900 mb-10 md:mb-14"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          What sets us apart
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 lg:gap-16 items-center">
          {/* Left column — video preview. `aspect-video` keeps the
              frame in step with the source webms which are all 16:9;
              rounded and shadowed so it reads as a poster inside the
              softer navy-50 section background. */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-2xl overflow-hidden bg-navy-900 aspect-video shadow-[0_25px_70px_-15px_rgba(2,8,20,0.35)]"
          >
            <video
              key={industry.mediaUrl}
              ref={videoRef}
              src={industry.mediaUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-navy-950/25"
            />
          </motion.div>

          {/* Right column — heading + description */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          >
            <h3 className="text-2xl sm:text-3xl md:text-[34px] lg:text-4xl font-bold text-navy-950 leading-[1.15] mb-6 md:mb-7">
              {industry.whatSetsApartHeading}
            </h3>
            <p className="text-base md:text-lg lg:text-xl text-navy-700 leading-relaxed">
              {industry.whatSetsApartDescription}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * "Explore our services" — full-bleed grid of all 11 What-We-Do
 * services, shaped exactly like the "Explore how we can help" grid
 * on /services. Placed at the bottom of every industry detail page
 * as a "here's what actually ships into your engagement" chaser.
 *
 * We reuse `<ServiceCard>` verbatim so any styling tweak to the
 * cards on /services propagates here automatically.
 */
const ExploreOurServicesSection: React.FC = () => (
  <section className="bg-white py-16 md:py-20 lg:py-24">
    <div className="site-wide-container">
      <motion.h2
        className="heading-lg text-navy-900 mb-10 md:mb-14"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        Explore our services
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 lg:gap-x-10 gap-y-14 md:gap-y-16">
        {WHAT_WE_DO_SERVICES.map((service, i) => (
          <ServiceCard
            key={service.id}
            service={service}
            animationDelay={Math.min(i, 5) * 0.06}
          />
        ))}
      </div>
    </div>
  </section>
);
